"use client";

import { useCallback, useEffect, useId, useSyncExternalStore } from "react";
import { FRONT_SLOT_SELECTOR } from "./roll-widget-slots";

/**
 * Le widget mis en avant : agrandi et centré sur le roll, par-dessus les
 * autres.
 *
 * # Un seul à la fois, donc un magasin partagé
 *
 * Deux widgets en avant se superposeraient au centre — il n'y a qu'un centre.
 * L'état ne peut donc pas vivre dans chaque widget, comme y vit son coin
 * (`useCornerDrag`) : mettre celui-ci en avant doit renvoyer celui-là. C'est
 * un magasin de module, comme `slotRefs` et pour la même raison — les widgets
 * sont rendus d'un peu partout dans le studio, et rien ne les surplombe qui
 * pourrait porter un contexte.
 *
 * # Ce n'est pas une fenêtre modale
 *
 * Pas de voile derrière : le rouleau est ce qu'on est venu regarder, et la
 * mise en avant n'est qu'un widget plus grand. Échap et un geste à côté le
 * renvoient dans son coin — lequel n'a pas bougé pendant ce temps.
 */

/** Le widget en avant, à son identité de rendu ; `null` quand personne ne l'est. */
let front: string | null = null;
const listeners = new Set<() => void>();

function publish(next: string | null): void {
  if (front === next) {
    return;
  }

  front = next;

  for (const listener of listeners) {
    listener();
  }
}

/** Met ce widget en avant, et renvoie dans son coin celui qui y était. */
export function promoteWidget(id: string): void {
  publish(id);
}

/**
 * Renvoie ce widget dans son coin. Sans effet s'il n'était pas en avant : un
 * widget qui se démonte ne doit pas décrocher celui qui vient de prendre sa
 * place.
 */
export function dismissWidget(id: string): void {
  if (front === id) {
    publish(null);
  }
}

/** Qui est en avant, pour les tests et pour `useSyncExternalStore`. */
export function frontWidget(): string | null {
  return front;
}

/** S'abonner aux changements de premier plan ; rend de quoi arrêter. */
export function subscribeFront(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

function serverSnapshot(): string | null {
  return null;
}

/** Ce qu'un widget a besoin de savoir et de pouvoir faire du premier plan. */
export interface WidgetFront {
  /** Ce widget est en avant. */
  readonly front: boolean;
  /** L'y mettre. */
  readonly promote: () => void;
  /** L'en sortir. */
  readonly dismiss: () => void;
}

/**
 * La mise en avant d'un widget : à appeler dans le widget lui-même, qui passe
 * `front` à son `RollWidget` et câble `promote` sur son bouton.
 *
 * L'identité vient de `useId` : elle est propre à cette instance et survit à
 * ses rendus, sans que personne ait à inventer ni à faire descendre une clé.
 */
export function useWidgetFront(): WidgetFront {
  const id = useId();
  const current = useSyncExternalStore(subscribeFront, frontWidget, serverSnapshot);
  const isFront = current === id;

  const promote = useCallback(() => promoteWidget(id), [id]);
  const dismiss = useCallback(() => dismissWidget(id), [id]);

  // Un widget qui s'en va ne laisse pas le premier plan occupé par personne :
  // la caméra qu'on coupe pendant qu'elle est en avant, par exemple.
  useEffect(() => () => dismissWidget(id), [id]);

  useEffect(() => {
    if (!isFront) {
      return;
    }

    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        dismissWidget(id);
      }
    };

    // `pointerdown` et non `click` : le geste qui a mis ce widget en avant est
    // déjà passé quand cet effet s'abonne, donc rien ne le renvoie aussitôt.
    // En capture, pour voir aussi les gestes qu'un widget arrête chez lui.
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target;

      if (target instanceof Element && target.closest(FRONT_SLOT_SELECTOR) !== null) {
        return;
      }

      dismissWidget(id);
    };

    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onPointerDown, true);

    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onPointerDown, true);
    };
  }, [id, isFront]);

  return { front: isFront, promote, dismiss };
}
